import React, {
  createContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
  useMemo,
} from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { siteUrl, vimeoUrl } from "../utils/constants";
import {
  SliderEntity,
  AboutEntity,
  ContactEntity,
  SponsorEntity,
  BackgroundImageEntity,
  ContributorEntity,
  CaseEntity,
  DocumentEntity,
  SocialMediaUrl,
  StrapiEntity,
  VideoEntity,
  VimeoVideo,
} from "../types/strapi";

interface ApiData {
  about: AboutEntity;
  backgroundImages: BackgroundImageEntity[];
  cases: CaseEntity[];
  contact: ContactEntity;
  contributors: ContributorEntity;
  documents: DocumentEntity[];
  home: SliderEntity[];
  socialMedia: SocialMediaUrl[];
  sponsors: SponsorEntity;
  tags: StrapiEntity[];
  videos: VideoEntity[];
  videosVimeo: VimeoVideo[];
}

interface LoadingState {
  about: boolean;
  backgroundImages: boolean;
  cases: boolean;
  contact: boolean;
  contributors: boolean;
  documents: boolean;
  home: boolean;
  socialMedia: boolean;
  sponsors: boolean;
  tags: boolean;
  videos: boolean;
  videosVimeo: boolean;
}

interface ErrorState {
  about: string | null;
  backgroundImages: string | null;
  cases: string | null;
  contact: string | null;
  contributors: string | null;
  documents: string | null;
  home: string | null;
  socialMedia: string | null;
  sponsors: string | null;
  tags: string | null;
  videos: string | null;
  videosVimeo: string | null;
}

interface DataContextType extends ApiData {
  archiveCollectionName: string | null;
  archiveCollectionItemId: string | null;
  locale: string;
  loading: LoadingState;
  errors: ErrorState;
  isLoading: boolean;
  hasErrors: boolean;
  changeLocale: (newLocale: string) => void;
  setArchiveCollectionName: (name: string | null) => void;
  setCollectionNameAndItemId: (
    collectionIdentifier: string,
    newId: string
  ) => void;
  refreshData: () => Promise<void>;
  clearErrors: () => void;
}

interface DataProviderProps {
  children: ReactNode;
}

// Cache for storing API responses by locale
const dataCache = new Map<string, ApiData>();
const CACHE_EXPIRY = 5 * 60 * 1000; // 5 minutes
const cacheTimestamps = new Map<string, number>();

// Helper function to check if cache is valid
const isCacheValid = (locale: string): boolean => {
  const cacheKey = `data_${locale}`;
  const timestamp = cacheTimestamps.get(cacheKey);
  return timestamp !== undefined && Date.now() - timestamp < CACHE_EXPIRY;
};

/**
 * DataContext provides centralized state management for all Strapi CMS data
 * Includes intelligent caching, loading states, and error handling
 */
export const DataContext = createContext<DataContextType | undefined>(
  undefined
);

/**
 * DataProvider component that manages all application data fetching and caching
 * @param props - Component props containing children elements
 * @returns Provider component wrapping children with data context
 * @example
 * <DataProvider>
 *   <App />
 * </DataProvider>
 */
export const DataProvider: React.FC<DataProviderProps> = ({ children }) => {
  const { i18n } = useTranslation();
  const [locale, setLocale] = useState<string>(i18n.language || "en");
  const [archiveCollectionName, setArchiveCollectionName] = useState<
    string | null
  >(null);
  const [archiveCollectionItemId, setArchiveCollectionItemId] = useState<
    string | null
  >(null);

  // Data states
  const [data, setData] = useState<ApiData>({
    about: {} as AboutEntity,
    backgroundImages: [],
    cases: [],
    contact: {} as ContactEntity,
    contributors: {} as ContributorEntity,
    documents: [],
    home: [],
    socialMedia: [],
    sponsors: {} as SponsorEntity,
    tags: [],
    videos: [],
    videosVimeo: [],
  });

  // Loading states
  const [loading, setLoading] = useState<LoadingState>({
    about: false,
    backgroundImages: false,
    cases: false,
    contact: false,
    contributors: false,
    documents: false,
    home: false,
    socialMedia: false,
    sponsors: false,
    tags: false,
    videos: false,
    videosVimeo: false,
  });

  // Error states
  const [errors, setErrors] = useState<ErrorState>({
    about: null,
    backgroundImages: null,
    cases: null,
    contact: null,
    contributors: null,
    documents: null,
    home: null,
    socialMedia: null,
    sponsors: null,
    tags: null,
    videos: null,
    videosVimeo: null,
  });

  // Utility functions for caching
  const getCachedData = (locale: string): ApiData | null => {
    if (isCacheValid(locale)) {
      return dataCache.get(`data_${locale}`) || null;
    }
    return null;
  };

  const setCachedData = (locale: string, newData: ApiData): void => {
    const cacheKey = `data_${locale}`;
    dataCache.set(cacheKey, newData);
    cacheTimestamps.set(cacheKey, Date.now());
  };

  // Fetch all data for a specific locale
  const fetchAllData = async (targetLocale: string): Promise<void> => {
    // Set all loading states to true
    setLoading({
      about: true,
      backgroundImages: true,
      cases: true,
      contact: true,
      contributors: true,
      documents: true,
      home: true,
      // socialMedia has no endpoint, so it is never in flight.
      socialMedia: false,
      sponsors: true,
      tags: true,
      videos: true,
      videosVimeo: true,
    });

    // Clear all errors
    setErrors({
      about: null,
      backgroundImages: null,
      cases: null,
      contact: null,
      contributors: null,
      documents: null,
      home: null,
      socialMedia: null,
      sponsors: null,
      tags: null,
      videos: null,
      videosVimeo: null,
    });

    // Each endpoint settles independently: its slice of data is committed to
    // state as soon as it arrives, so a page renders on the request it needs
    // rather than waiting for the slowest one. A failure is contained to its
    // own key instead of blanking every content type.
    // socialMedia has no endpoint; it is always empty but belongs in a
    // complete cache entry.
    const accumulated: Partial<ApiData> = { socialMedia: [] };

    const tasks: {
      key: keyof ApiData;
      request: () => Promise<{ data: { data: unknown } }>;
      select: (payload: any) => unknown;
    }[] = [
      {
        // List view only: no chapter* populates. The chapters are ~90% of this
        // payload and only a single case's are ever read, so CaseDetails
        // fetches the one it needs by documentId instead. backgroundImage and
        // localizations stay — the cards render the first and slug resolution
        // (including Spanish URLs) depends on the second.
        key: "cases",
        request: () =>
          axios.get(
            `${siteUrl}/api/cases?locale=${targetLocale}&populate[backgroundImage][populate]=*&populate[localizations][populate]=*`
          ),
        select: (payload) => payload.data,
      },
      {
        key: "about",
        request: () =>
          axios.get(
            `${siteUrl}/api/abouts?locale=${targetLocale}&populate[news][populate]=*`
          ),
        select: (payload) => payload.data[0] || {},
      },
      {
        key: "backgroundImages",
        request: () =>
          axios.get(
            `${siteUrl}/api/background-images?locale=${targetLocale}&populate=*`
          ),
        select: (payload) => payload.data,
      },
      {
        key: "documents",
        request: () =>
          axios.get(
            `${siteUrl}/api/documents?locale=${targetLocale}&populate[contentType][populate]=*&populate[file][populate]=*`
          ),
        select: (payload) => payload.data,
      },
      {
        key: "contact",
        request: () =>
          axios.get(
            `${siteUrl}/api/contacts?locale=${targetLocale}&populate[formText][populate]=*`
          ),
        select: (payload) => payload.data[0] || {},
      },
      {
        key: "contributors",
        request: () =>
          axios.get(
            `${siteUrl}/api/contributors?locale=${targetLocale}&populate[contributor][populate]=*`
          ),
        select: (payload) => payload?.data?.[0] || {},
      },
      {
        key: "home",
        request: () =>
          axios.get(`${siteUrl}/api/sliders?locale=${targetLocale}&populate=*`),
        select: (payload) => payload.data,
      },
      {
        key: "sponsors",
        request: () =>
          axios.get(
            `${siteUrl}/api/sponsors?locale=${targetLocale}&populate=*`
          ),
        select: (payload) => payload.data[0] || {},
      },
      {
        key: "tags",
        request: () => axios.get(`${siteUrl}/api/tags?locale=${targetLocale}`),
        select: (payload) => payload.data,
      },
      {
        key: "videos",
        request: () =>
          axios.get(
            `${siteUrl}/api/videos?locale=${targetLocale}&populate[contentType][populate]=*&populate[resources][populate]=*`
          ),
        select: (payload) => payload.data,
      },
      {
        key: "videosVimeo",
        request: () =>
          axios.get(vimeoUrl, {
            headers: {
              Authorization: `Bearer ${
                import.meta.env.VITE_VIMEO_ACCESS_TOKEN
              }`,
            },
          }),
        select: (payload) => payload.data,
      },
    ];

    await Promise.all(
      tasks.map(async ({ key, request, select }) => {
        try {
          const response = await request();
          const value = select(response.data);

          accumulated[key] = value as never;

          // Commit this slice immediately so the UI can render against it.
          setData((previous) => ({ ...previous, [key]: value }));
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : "Failed to load data";
          setErrors((previous) => ({ ...previous, [key]: errorMessage }));
        } finally {
          setLoading((previous) => ({ ...previous, [key]: false }));
        }
      })
    );

    // Only cache a fully successful fetch, so a partial result never gets
    // served as complete for the rest of the cache window.
    if (Object.keys(accumulated).length === tasks.length + 1) {
      setCachedData(targetLocale, accumulated as ApiData);
    }
  };

  // Locale change handler
  const changeLocale = useCallback(
    (newLocale: string): void => {
      if (newLocale === locale) {
        return;
      }

      setLocale(newLocale);
      i18n.changeLanguage(newLocale);
    },
    [locale, i18n]
  );

  const setCollectionNameAndItemId = useCallback(
    (collectionIdentifier: string, newId: string): void => {
      setArchiveCollectionName(collectionIdentifier);
      setArchiveCollectionItemId(newId);
    },
    []
  );

  // Refresh data function - clears cache and refetches
  const refreshData = useCallback(async (): Promise<void> => {
    const cacheKey = `data_${locale}`;
    dataCache.delete(cacheKey);
    cacheTimestamps.delete(cacheKey);

    await fetchAllData(locale);
  }, [locale]); // eslint-disable-line react-hooks/exhaustive-deps

  const clearErrors = useCallback((): void => {
    setErrors({
      about: null,
      backgroundImages: null,
      cases: null,
      contact: null,
      contributors: null,
      documents: null,
      home: null,
      socialMedia: null,
      sponsors: null,
      tags: null,
      videos: null,
      videosVimeo: null,
    });
  }, []);

  // Main effect to load data when locale changes
  useEffect(() => {
    const cachedData = getCachedData(locale);

    if (cachedData) {
      // Use cached data if available and valid
      setData(cachedData);
      // Reset loading and error states
      setLoading({
        about: false,
        backgroundImages: false,
        cases: false,
        contact: false,
        contributors: false,
        documents: false,
        home: false,
        socialMedia: false,
        sponsors: false,
        tags: false,
        videos: false,
        videosVimeo: false,
      });
      setErrors({
        about: null,
        backgroundImages: null,
        cases: null,
        contact: null,
        contributors: null,
        documents: null,
        home: null,
        socialMedia: null,
        sponsors: null,
        tags: null,
        videos: null,
        videosVimeo: null,
      });
    } else {
      // Fetch fresh data if no cache or cache expired
      fetchAllData(locale);
    }
  }, [locale]); // eslint-disable-line react-hooks/exhaustive-deps

  // Computed values
  const isLoading = useMemo(
    () => Object.values(loading).some((isLoading) => isLoading),
    [loading]
  );

  const hasErrors = useMemo(
    () => Object.values(errors).some((error) => error !== null),
    [errors]
  );

  const contextValue = useMemo(
    () => ({
      // Data
      ...data,
      archiveCollectionName,
      archiveCollectionItemId,
      locale,

      // States
      loading,
      errors,
      isLoading,
      hasErrors,

      // Actions
      changeLocale,
      setArchiveCollectionName,
      setCollectionNameAndItemId,
      refreshData,
      clearErrors,
    }),
    [
      data,
      archiveCollectionName,
      archiveCollectionItemId,
      locale,
      loading,
      errors,
      isLoading,
      hasErrors,
      changeLocale,
      setArchiveCollectionName,
      setCollectionNameAndItemId,
      refreshData,
      clearErrors,
    ]
  );

  return (
    <DataContext.Provider value={contextValue}>{children}</DataContext.Provider>
  );
};
